import { useState, useContext } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../context/ModuleContext";

const WSU_RED = "#A60F2D";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const ValidateIdentity = ({ userId }) => {
  const [state, setState] = useState({
    validating: false,
    error: null,
    selectedOption: "",
    message: "Detail the validation method used here...",
  });

  const { setModule } = useContext(ModuleContext);
  const { authState } = useOktaAuth();

  const alternateValidate = async () => {
    if (!state.selectedOption) {
      setState((prev) => ({ ...prev, error: { message: "You must select a validation option" } }));
      return;
    }

    setState((prev) => ({ ...prev, validating: true }));

    let theMessage =
      state.selectedOption === "Verified in Person"
        ? "Verified in person"
        : state.selectedOption === "Verified Over Zoom"
        ? "Verified over zoom"
        : state.message;

    await fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/logs`, {
      body: JSON.stringify({
        target_username: userId,
        actor_username: authState.accessToken.claims.sub,
        action: state.selectedOption,
        comment: theMessage,
        api_process: "validateUser",
        user_process: state.selectedOption,
      }),
      method: "POST",
      headers: {
        Authorization: authState.accessToken.accessToken,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((res) => res.json())
      .then(
        () => setModule((prev) => ({ ...prev, validated: true })),
        (error) => setModule((prev) => ({ ...prev, validationError: error.message }))
      );
  };

  if (state.validating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Error Banner */}
      {state.error && (
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Validation failed
          </Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      )}

      {/* ── Section: Validation Options ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Validation Options
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{userId}</Typography>
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        <RadioGroup
          value={state.selectedOption}
          onChange={(e) => setState((prev) => ({ ...prev, selectedOption: e.target.value }))}
        >
          <FormControlLabel
            value="Verified in Person"
            control={<Radio sx={{ color: WSU_RED, "&.Mui-checked": { color: WSU_RED } }} />}
            label="Verified In Person"
          />
          <FormControlLabel
            value="Verified Over Zoom"
            control={<Radio sx={{ color: WSU_RED, "&.Mui-checked": { color: WSU_RED } }} />}
            label="Verified Over Zoom"
          />
          <FormControlLabel
            value="Verified Other"
            control={<Radio sx={{ color: WSU_RED, "&.Mui-checked": { color: WSU_RED } }} />}
            label="Other"
          />
        </RadioGroup>
      </Box>

      {state.selectedOption === "Verified Other" && (
        <>
          <Divider sx={{ mx: 3 }} />
          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
              Details
            </Typography>
          </Box>
          <Box sx={{ px: 3, pb: 3 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={4}
              label="Validation Details"
              value={state.message}
              onChange={(e) => setState((prev) => ({ ...prev, message: e.target.value }))}
              sx={fieldSx}
            />
          </Box>
        </>
      )}

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={alternateValidate}
          disabled={!state.selectedOption}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
            "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
          }}
        >
          Verify
        </Button>
      </Box>

    </TableContainer>
  );
};
